import { X } from 'lucide-react';
import { Dialog as D } from 'radix-ui';
import { type ReactNode } from 'react';

import { cx } from '@/lib/cx';

import { Button } from './button';

interface DialogProps {
  aberto: boolean;
  aoMudar: (aberto: boolean) => void;
  titulo: string;
  descricao?: ReactNode;
  children: ReactNode;
  /** Botões do rodapé (ação principal por último). */
  acoes?: ReactNode;
  className?: string;
}

export const estiloVeu =
  'fixed inset-0 z-40 bg-veu backdrop-blur-xs data-[state=open]:animate-surgir';

/**
 * Janela modal centralizada para confirmações e formulários curtos. Nunca passa da tela: título e
 * botões ficam fixos e só o conteúdo rola (no celular a janela não escondia o rodapé — 14/10/2026).
 */
export function Dialog({
  aberto,
  aoMudar,
  titulo,
  descricao,
  children,
  acoes,
  className,
}: DialogProps) {
  return (
    <D.Root open={aberto} onOpenChange={aoMudar}>
      <D.Portal>
        <D.Overlay className={estiloVeu} />
        <D.Content
          className={cx(
            'fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-dialogo -translate-x-1/2 -translate-y-1/2 flex-col gap-4',
            'rounded-painel border border-borda bg-superficie-elevada p-4 shadow-alta data-[state=open]:animate-surgir sm:p-6',
            className,
          )}
        >
          <div className="grid gap-1 pr-8">
            <D.Title className="text-lg font-semibold">{titulo}</D.Title>
            {descricao ? (
              <D.Description className="text-sm text-texto-suave">{descricao}</D.Description>
            ) : (
              <D.Description className="sr-only">{titulo}</D.Description>
            )}
          </div>
          {/* -m/p: o anel de foco dos campos não é cortado pela rolagem. */}
          <div className="-mx-4 -my-1 grid min-h-0 flex-1 gap-4 overflow-y-auto px-4 py-1 sm:-mx-6 sm:px-6">
            {children}
          </div>
          {acoes && <div className="flex flex-wrap justify-end gap-2">{acoes}</div>}
          <D.Close asChild>
            <Button
              variante="fantasma"
              tamanho="icone-sm"
              className="absolute right-3 top-3"
              aria-label="Fechar"
            >
              <X aria-hidden />
            </Button>
          </D.Close>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
